import React, { useEffect, useRef, useState } from "react";

import "./App.css";
import { fallbackHomepageData } from "./fallbackHomepageData";
import {
  buildPathForLocale,
  getClientBrowserLanguage,
  getClientTimezone,
  getInitialLocalePreference,
  getLocaleFromPath,
  getQueryHints,
  normaliseCountryCode,
  normaliseLanguageCode,
  persistLocale,
  resolveLocaleForCountryChange,
  resolveLocaleForLanguageChange,
  stripLocaleHintParams,
} from "./localeUtils";
import { applySeoToDocument } from "./seoClient";

const MOBILE_IFRAME_BREAKPOINT = 720;
const IFRAME_BASE_URL =
  "https://wiz.directferries.com/partners/deal_finder_iframe.aspx";
const IFRAME_STYLE_PARAMS = {
  btn: "47a347",
  btnh: "168b16",
  btnt: "FFFFFF",
  lbld: "700",
  pclr: "62d0db",
  sclr: "62d0db",
  stdc: "F7907",
  targ: "0",
  tclr: "0B2231",
  lclr: "EAF6FB",
};

function useMediaQuery(maxWidth, initialMatch = false) {
  const query = `(max-width: ${maxWidth}px)`;
  const [matches, setMatches] = useState(initialMatch);

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return undefined;
    }

    const mediaQuery = window.matchMedia(query);
    const handleChange = (event) => {
      setMatches(event.matches);
    };

    setMatches(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, [query]);

  return matches;
}

function getLocationState() {
  if (typeof window === "undefined") {
    return {
      queryCountry: "",
      queryLanguage: "",
      requestedLocale: getInitialLocalePreference(),
      search: "",
    };
  }

  const pathLocale = getLocaleFromPath(window.location.pathname);
  const queryHints = getQueryHints(window.location.search);

  return {
    queryCountry: queryHints.country,
    queryLanguage: queryHints.language,
    requestedLocale: pathLocale || getInitialLocalePreference(),
    search: window.location.search,
  };
}

function getPreloadedHomeData() {
  if (typeof window === "undefined") {
    return null;
  }

  const payload = window.__FERRYSEARCH_INITIAL_DATA__;
  return payload && typeof payload === "object" ? payload : null;
}

function buildHomepageApiUrl({ locale, country, language }) {
  const url = new URL("/api/homepage", window.location.origin);

  if (locale) {
    url.searchParams.set("locale", locale);
  } else {
    if (country) {
      url.searchParams.set("country", country);
    }

    if (language) {
      url.searchParams.set("language", language);
    }
  }

  const browserLanguage = getClientBrowserLanguage();
  const timezone = getClientTimezone();

  if (browserLanguage) {
    url.searchParams.set("browserLanguage", browserLanguage);
  }

  if (timezone) {
    url.searchParams.set("timezone", timezone);
  }

  return url.toString();
}

async function postAnalyticsEvent(event) {
  try {
    await fetch("/api/analytics", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(event),
      keepalive: true,
    });
  } catch (error) {
    // First-party analytics should never block the user journey.
  }
}

function buildFallbackGradient(slug) {
  let seed = 0;
  for (const character of slug) {
    seed += character.charCodeAt(0);
  }

  const hue = seed % 50;

  return {
    backgroundImage: `linear-gradient(135deg, hsla(${190 + hue}, 55%, 18%, 0.95), hsla(${205 + hue}, 58%, 32%, 0.78))`,
  };
}

function buildIframeUrl({ culture, destinationPort, originPort, useMobileIframe }) {
  const url = new URL(IFRAME_BASE_URL);

  Object.entries(IFRAME_STYLE_PARAMS).forEach(([key, value]) => {
    url.searchParams.set(key, value);
  });

  if (useMobileIframe) {
    url.searchParams.set("vert", "1");
  }

  if (culture) {
    url.searchParams.set("cult", culture);
  }

  if (originPort) {
    url.searchParams.set("oprt", originPort);
  }

  if (destinationPort) {
    url.searchParams.set("rprt", destinationPort);
  }

  return url.toString();
}

function App({ initialHomeData = null, initialLocationState = null } = {}) {
  const preloadedHomeDataRef = useRef(
    initialHomeData || getPreloadedHomeData(),
  );
  const analyticsKeyRef = useRef("");
  const [homeData, setHomeData] = useState(
    () => preloadedHomeDataRef.current || fallbackHomepageData,
  );
  const [isLoadingHomepage, setIsLoadingHomepage] = useState(
    () => !preloadedHomeDataRef.current,
  );
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [locationState, setLocationState] = useState(
    () => initialLocationState || getLocationState(),
  );
  const useMobileIframe = useMediaQuery(MOBILE_IFRAME_BREAKPOINT);

  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      window.__FERRYSEARCH_INITIAL_DATA__
    ) {
      delete window.__FERRYSEARCH_INITIAL_DATA__;
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handlePopState = () => {
      setLocationState(getLocationState());
    };

    window.addEventListener("popstate", handlePopState);

    const pathLocale = getLocaleFromPath(window.location.pathname);
    if (!pathLocale && locationState.requestedLocale) {
      const nextSearch = stripLocaleHintParams(window.location.search);
      window.history.replaceState(
        {},
        "",
        `${buildPathForLocale(locationState.requestedLocale, nextSearch)}${window.location.hash || ""}`,
      );
      setLocationState(getLocationState());
    }

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [locationState.requestedLocale]);

  useEffect(() => {
    let isCancelled = false;

    const preloadedHomeData = preloadedHomeDataRef.current;
    if (
      preloadedHomeData &&
      preloadedHomeData.resolvedLocale?.locale ===
        locationState.requestedLocale &&
      !locationState.queryCountry &&
      !locationState.queryLanguage
    ) {
      preloadedHomeDataRef.current = null;
      setIsLoadingHomepage(false);

      return () => {
        isCancelled = true;
      };
    }

    async function loadHomepage() {
      setIsLoadingHomepage(true);

      try {
        const response = await fetch(
          buildHomepageApiUrl({
            locale: locationState.requestedLocale,
            country: locationState.queryCountry,
            language: locationState.queryLanguage,
          }),
          {
            headers: {
              Accept: "application/json",
            },
          },
        );

        if (!response.ok) {
          throw new Error(`Homepage request failed: ${response.status}`);
        }

        const nextData = await response.json();

        if (isCancelled) {
          return;
        }

        setHomeData(nextData);
        persistLocale(nextData.resolvedLocale.locale);

        const pathLocale =
          typeof window === "undefined"
            ? locationState.requestedLocale
            : getLocaleFromPath(window.location.pathname);
        const strippedSearch =
          typeof window === "undefined"
            ? ""
            : stripLocaleHintParams(window.location.search);

        if (
          typeof window !== "undefined" &&
          (pathLocale !== nextData.resolvedLocale.locale ||
            window.location.search !== strippedSearch)
        ) {
          window.history.replaceState(
            {},
            "",
            `${buildPathForLocale(nextData.resolvedLocale.locale, strippedSearch)}${window.location.hash || ""}`,
          );
          setLocationState(getLocationState());
        }
      } catch (error) {
        if (!isCancelled) {
          setHomeData((currentData) => currentData || fallbackHomepageData);
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingHomepage(false);
        }
      }
    }

    loadHomepage();

    return () => {
      isCancelled = true;
    };
  }, [
    locationState.queryCountry,
    locationState.queryLanguage,
    locationState.requestedLocale,
  ]);

  const bookingEngineUrl = buildIframeUrl({
    culture: homeData.iframe.culture,
    destinationPort: homeData.iframe.destinationPort,
    originPort: homeData.iframe.originPort,
    useMobileIframe,
  });

  useEffect(() => {
    setIframeLoaded(false);
  }, [bookingEngineUrl]);

  useEffect(() => {
    const analyticsKey = `${homeData.resolvedLocale.locale}:${homeData.routes
      .map((route) => route.slug)
      .join("|")}:${homeData.iframe.culture}:${homeData.iframe.defaultRouteSlug}`;

    if (!analyticsKey || analyticsKeyRef.current === analyticsKey) {
      return;
    }

    analyticsKeyRef.current = analyticsKey;

    const imageUsage = homeData.routes.reduce(
      (summary, route) => {
        if (route.image?.source === "unsplash") {
          summary.unsplash += 1;
        } else if (route.image?.source === "generated") {
          summary.generated += 1;
        } else {
          summary.fallback += 1;
        }

        return summary;
      },
      { unsplash: 0, generated: 0, fallback: 0 },
    );

    postAnalyticsEvent({
      type: "locale_resolved",
      browserLanguage: homeData.resolvedLocale.browserLanguage,
      detectedCountry: homeData.resolvedLocale.detectedCountry,
      detectedLanguage: homeData.resolvedLocale.detectedLanguage,
      locale: homeData.resolvedLocale.locale,
      source: homeData.resolvedLocale.source,
    });

    postAnalyticsEvent({
      type: "route_impression",
      country: homeData.resolvedLocale.countryCode,
      language: homeData.resolvedLocale.languageCode,
      locale: homeData.resolvedLocale.locale,
      routes: homeData.routes.map((route) => route.slug),
    });

    postAnalyticsEvent({
      type: "image_usage",
      imageUsage,
      locale: homeData.resolvedLocale.locale,
    });

    postAnalyticsEvent({
      type: "iframe_config",
      culture: homeData.iframe.culture,
      defaultRouteSlug: homeData.iframe.defaultRouteSlug,
      locale: homeData.resolvedLocale.locale,
    });
  }, [homeData]);

  useEffect(() => {
    applySeoToDocument(homeData.seo);
  }, [homeData.seo]);

  const navigateToLocale = (nextLocale, replace = false) => {
    if (typeof window === "undefined") {
      return;
    }

    const nextSearch = stripLocaleHintParams(window.location.search);
    const nextUrl = `${buildPathForLocale(nextLocale, nextSearch)}${window.location.hash || ""}`;
    const historyMethod = replace ? "replaceState" : "pushState";

    window.history[historyMethod]({}, "", nextUrl);
    persistLocale(nextLocale);
    setLocationState(getLocationState());
  };

  const handleCountryChange = (event) => {
    const nextCountry = normaliseCountryCode(event.target.value);
    if (!nextCountry || nextCountry === homeData.resolvedLocale.countryCode) {
      return;
    }

    const nextLocale = resolveLocaleForCountryChange(
      nextCountry,
      homeData.resolvedLocale.languageCode,
    );

    postAnalyticsEvent({
      type: "country_change",
      fromCountry: homeData.resolvedLocale.countryCode,
      toCountry: nextCountry,
      locale: nextLocale,
    });
    postAnalyticsEvent({
      type: "locale_change",
      fromLocale: homeData.resolvedLocale.locale,
      toLocale: nextLocale,
      trigger: "country",
    });

    navigateToLocale(nextLocale);
  };

  const handleLanguageChange = (event) => {
    const nextLanguage = normaliseLanguageCode(event.target.value);
    if (!nextLanguage || nextLanguage === homeData.resolvedLocale.languageCode) {
      return;
    }

    const nextLocale = resolveLocaleForLanguageChange(
      homeData.resolvedLocale.countryCode,
      nextLanguage,
    );

    postAnalyticsEvent({
      type: "language_change",
      fromLanguage: homeData.resolvedLocale.languageCode,
      toLanguage: nextLanguage,
      locale: nextLocale,
    });
    postAnalyticsEvent({
      type: "locale_change",
      fromLocale: homeData.resolvedLocale.locale,
      toLocale: nextLocale,
      trigger: "language",
    });

    navigateToLocale(nextLocale);
  };

  const handleRouteClick = (route) => {
    postAnalyticsEvent({
      type: "route_click",
      country: homeData.resolvedLocale.countryCode,
      language: homeData.resolvedLocale.languageCode,
      locale: homeData.resolvedLocale.locale,
      route: route.slug,
    });
  };

  return (
    <div className="site-shell">
      <div className="site-ornaments" aria-hidden="true">
        <span className="ornament ornament-left" />
        <span className="ornament ornament-right" />
        <span className="ornament ornament-bottom" />
      </div>

      <header className="site-header">
        <a className="brand" href={`/${homeData.resolvedLocale.locale}`} aria-label="FerrySearch home">
          <span className="brand-wordmark" aria-hidden="true">
            <span className="brand-wordmark-main">Ferry</span>
            <span className="brand-wordmark-accent">Search</span>
          </span>
          <span className="brand-tagline">{homeData.copy.nav.brandTagline}</span>
        </a>

        <nav className="site-nav" aria-label="Primary navigation">
          <a href="#search">{homeData.copy.nav.navSearch}</a>
          <a href="#routes">{homeData.copy.nav.navRoutes}</a>
          <a href="#why">{homeData.copy.nav.navWhy}</a>
        </nav>

        <form
          className="locale-controls"
          action="/resolve-locale"
          method="get"
          aria-label="Locale controls"
        >
          <label className="locale-switcher">
            <span>{homeData.copy.nav.languageLabel}</span>
            <div className="select-wrap">
              <select
                name="language"
                value={homeData.resolvedLocale.languageCode}
                onChange={handleLanguageChange}
              >
                {homeData.availableLanguages.map((language) => (
                  <option key={language.code} value={language.code}>
                    {language.name}
                  </option>
                ))}
              </select>
            </div>
          </label>

          <label className="locale-switcher">
            <span>{homeData.copy.nav.countryLabel}</span>
            <div className="select-wrap">
              <select
                name="country"
                value={homeData.resolvedLocale.countryCode}
                onChange={handleCountryChange}
              >
                {homeData.availableCountries.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>
          </label>
          <button className="locale-submit secondary-button" type="submit">
            {homeData.copy.nav.applyLabel}
          </button>
        </form>
      </header>

      <main>
        <section className="hero" id="top">
          <div className="hero-copy">
            <div className="hero-pills">
              {homeData.copy.hero.pills.map((pill) => (
                <span key={pill}>{pill}</span>
              ))}
            </div>

            <p className="eyebrow">{homeData.copy.hero.eyebrow}</p>
            <h1>{homeData.copy.hero.title}</h1>
            <p className="hero-text">{homeData.copy.hero.description}</p>

            <div className="hero-actions">
              <a className="primary-button" href="#search">
                {homeData.copy.hero.primaryCta}
              </a>
              <a className="secondary-button" href="#routes">
                {homeData.copy.hero.secondaryCta}
              </a>
            </div>
          </div>

          <aside className="search-column" id="search">
            <div className="search-card">
              <div className="search-card-header">
                <div>
                  <p className="search-label">{homeData.copy.search.kicker}</p>
                  <h2>{homeData.copy.search.title}</h2>
                  <p className="search-intro">{homeData.copy.search.description}</p>
                </div>
                <span className="status-pill">
                  {useMobileIframe
                    ? homeData.copy.search.statusMobile
                    : homeData.copy.search.statusDesktop}
                </span>
              </div>

              <div className={`iframe-shell${useMobileIframe ? " is-mobile" : ""}`}>
                {!iframeLoaded && (
                  <div className="iframe-loading" aria-hidden="true">
                    <div className="loading-bars">
                      <span />
                      <span />
                      <span />
                    </div>
                    <p>{homeData.copy.search.loadingText}</p>
                  </div>
                )}

                <iframe
                  key={bookingEngineUrl}
                  src={bookingEngineUrl}
                  title="Ferry booking search"
                  className={`booking-iframe${useMobileIframe ? " is-mobile" : ""}${iframeLoaded ? " is-loaded" : ""}`}
                  loading="lazy"
                  onLoad={() => setIframeLoaded(true)}
                  referrerPolicy="strict-origin-when-cross-origin"
                  scrolling="no"
                />
              </div>

              <p className="search-note">{homeData.copy.search.note}</p>
            </div>
          </aside>
        </section>

        <section className="content-section deferred-section" id="routes">
          <div className="section-header">
            <div className="section-intro">
              <p className="section-kicker">{homeData.copy.routes.kicker}</p>
              <h2>{homeData.copy.routes.heading}</h2>
              <p>{homeData.copy.routes.description}</p>
            </div>

            <a className="secondary-button section-link" href={homeData.allRoutesUrl}>
              {homeData.copy.routes.viewAllLabel}
            </a>
          </div>

          <div className="route-grid">
            {homeData.routes.map((route) => (
              <article className="route-card" key={route.slug}>
                <div
                  className="route-visual"
                  style={!route.image ? buildFallbackGradient(route.slug) : undefined}
                >
                  {route.image ? (
                    <img
                      src={route.image.smallUrl || route.image.url}
                      alt={route.image.alt}
                      decoding="async"
                      fetchPriority="low"
                      height="360"
                      loading="lazy"
                      sizes="(max-width: 720px) calc(100vw - 2rem), (max-width: 1120px) calc(50vw - 2rem), 33vw"
                      width="600"
                    />
                  ) : (
                    <div className="route-visual-fallback" aria-hidden="true" />
                  )}
                  <div className="route-visual-overlay" />
                  <div className="route-visual-meta">
                    {route.tag ? <span className="route-tag">{route.tag}</span> : null}
                    {route.duration ? <span className="route-duration">{route.duration}</span> : null}
                  </div>
                </div>

                <div className="route-content">
                  <h3>{route.title}</h3>
                  <p className="route-tagline">{route.tagline}</p>
                  <p className="route-description">{route.description}</p>

                  <div className="route-footer">
                    <a
                      className="route-link-button"
                      href={route.href}
                      onClick={() => handleRouteClick(route)}
                    >
                      {route.cta}
                    </a>

                    {route.image && route.image.source === "unsplash" ? (
                      <p className="route-credit">
                        Photo by{" "}
                        <a href={route.image.creditUrl} target="_blank" rel="noreferrer">
                          {route.image.creditName}
                        </a>{" "}
                        on{" "}
                        <a href={route.image.collectionUrl} target="_blank" rel="noreferrer">
                          Unsplash
                        </a>
                      </p>
                    ) : (
                      <p className="route-credit">{homeData.copy.ui.routeCreditFallback}</p>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="content-section why-section deferred-section" id="why">
          <div className="section-intro why-intro">
            <p className="section-kicker">{homeData.copy.nav.navWhy}</p>
            <h2>{homeData.copy.why.heading}</h2>
            <p>{homeData.copy.why.intro}</p>
          </div>

          <div className="why-grid">
            {homeData.copy.why.points.map((card) => (
              <article className="why-card" key={card.title}>
                <h3>{card.title}</h3>
                <p>{card.description}</p>
              </article>
            ))}
          </div>

          <p className="why-footer">{homeData.copy.why.footer}</p>
        </section>
      </main>

      <footer className="site-footer deferred-section">
        <div>
          <strong>FerrySearch</strong>
          <p>{homeData.copy.footer.blurb}</p>
        </div>
        <p className="footer-note">
          {isLoadingHomepage
            ? homeData.copy.footer.loadingText
            : homeData.copy.footer.liveNote}
        </p>
      </footer>

      <noscript>
        <div className="no-js-note">
          <p>
            FerrySearch works without JavaScript for core content and
            navigation. Instant locale switching and enhanced interactions work
            better when scripts are available.
          </p>
        </div>
      </noscript>

      <div className="mobile-cta">
        <span>{homeData.copy.ui.mobilePrompt}</span>
        <a href="#search" className="mobile-cta-link">
          {homeData.copy.ui.mobileButton}
        </a>
      </div>
    </div>
  );
}

export default App;
